import React, { useCallback } from 'react'
import Link from 'components/Link'
import Button from 'components/Button'
import PropTypes from 'prop-types'
import { BottomWhiteCurvedEdge, Spacing60, Spacing20 } from 'styles/sharedStyle'
import animationUtils from 'utils/animation'
import scrollTo from 'utils/scrollTo'
import { Eyebrow, CtaLabel, Title70 } from 'components/Typography'
import VideoPlayer from 'components/VideoPlayer'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'
import { globalHistory as history } from '@reach/router'
import { useComponentId, useFieldId } from 'utils/optimizely'
import ArrowSettingsIcon from 'components/Icons/ArrowSettingsIcon'
import Arrow from 'components/Arrow/Arrow'
import FadeInWrapper from 'components/FadeInWrapper'
import { fixAudiences } from '../../utils/audiences'
import {
  ExploreCue,
  HeroBody,
  HeroCopy,
  HeroCta,
  HeroEyebrow,
  HeroImage,
  HeroImg,
  HeroLogo,
  HeroTitle,
  HeroWrapper,
  SearchButtonIcon,
  ShareBtn,
  ShareCta,
  CenteredContainer,
  HeroOpenHours,
} from './Hero.style'
import videoPlayerUtils from '../VideoPlayer/videoPlayerUtils'

const Hero = ({
  audiences,
  isCondensed,
  opacity,
  content,
  ctaLabel,
  ctaUrl,
  eyebrow,
  fullScreen,
  image,
  isBrandDirectory,
  logo,
  pageTemplate,
  share,
  toggleBrandSearch,
  video,
  villageSlug,
  openHoursLabel,
}) => {
  const { landscape, portrait, altText } = image

  const variant =
    {
      ContentfulPageTemplateMemorableDaysT14: 'memDays',
    }[pageTemplate] || 'default'

  const delayObj = animationUtils.calculateDelay({
    logo,
    eyebrow,
    content,
    cta: ctaLabel && (ctaUrl || (isBrandDirectory && toggleBrandSearch)),
  })

  const opacities = {
    '0%': 0,
    '15%': 0.15,
    '30%': 0.3,
  }

  const overlayOpacity = opacity ? opacities[opacity] : '0.15'
  const background = `rgba(0, 0, 0, ${overlayOpacity});`

  const hasImage =
    portrait &&
    portrait.gatsbyImageData &&
    landscape &&
    landscape.gatsbyImageData
  const heroImageProps = {
    showOverlay:
      (content || eyebrow || logo) && hasImage && variant !== 'memDays',
    variant,
    background,
  }

  const hasVideo = video && video.portrait && video.landscape

  if (hasVideo) {
    // eslint-disable-next-line no-param-reassign
    video.opacity = videoPlayerUtils.videoPlayerHeroOpacityOverride(
      video,
      overlayOpacity
    )
  }

  const hasAudiences = !!audiences

  const {
    location: { pathname },
  } = history

  const arrowProps = {
    colour: '',
    marginTop: '60px',
    animation: true,
  }

  const redirectToOffers = () => {
    const offersElement = document.getElementById('openinghours')
    if (offersElement) {
      offersElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const shareScroll = () => {
    const { id: shareId } = share || {}
    const el = shareId && document.getElementById(shareId)
    if (el) {
      const y = Math.min(
        el.offsetTop,
        document.body.scrollHeight - window.innerHeight
      )
      scrollTo(y, 800)
    }
  }

  const onSearchHeroClick = useCallback((e) => {
    e.preventDefault()
    toggleBrandSearch()
    trackGTM('brands', 'brands', 'brand search box hero', pathname)

    GA4('brands_page_hero_search', {
      village_name: villageSlug,
    })
  }, [])

  const elementId = useComponentId(`Comp_hero_HER01`)

  return (
    <HeroWrapper
      id={elementId}
      condensed={isCondensed}
      fullScreen={fullScreen}
      variant={variant}
      className={fixAudiences(audiences)}
      hasAudience={hasAudiences}
      hasImage={hasImage}
      openHoursLabel={!!openHoursLabel}>
      <HeroImage {...heroImageProps}>
        {hasVideo && (
          <VideoPlayer
            id={useFieldId(elementId, `video`)}
            name={video.name || content}
            controls={false}
            loop
            muted
            video={video}
            autoplay
            image={{ portrait, landscape, altText }}
            noLazyload
            villageSlug={villageSlug}
          />
        )}
        {hasImage && !hasVideo && (
          <>
            <HeroImg
              id={useFieldId(elementId, `image`)}
              image={portrait.gatsbyImageData}
              display={['block', null, 'none']}
              alt={altText}
              loading="eager"
            />
            <HeroImg
              id={useFieldId(elementId, `image`)}
              image={landscape.gatsbyImageData}
              display={['none', null, 'block']}
              alt={altText}
              loading="eager"
            />
          </>
        )}
      </HeroImage>
      <HeroBody variant={variant}>
        {logo && logo.code && logo.code.code && (
          <>
            <HeroLogo
              id={useFieldId(elementId, `body-logo`)}
              dangerouslySetInnerHTML={{ __html: logo.code.code }}
              delay={delayObj.logo}
            />
          </>
        )}
        {eyebrow && (
          <>
            <HeroEyebrow delay={delayObj.eyebrow}>
              <Eyebrow
                id={useFieldId(elementId, `body-eyebrow`)}
                className="HeroEyebrow">
                {eyebrow}
              </Eyebrow>
            </HeroEyebrow>
          </>
        )}
        {content && (
          <>
            {(eyebrow || logo) && variant !== 'memDays' && <Spacing20 />}
            <HeroCopy delay={delayObj.content}>
              <Title70
                as={HeroTitle}
                variant={variant}
                className="HeroTitle"
                id={useFieldId(elementId, `title`)}>
                {content}
              </Title70>
              {!!share && (
                <>
                  <Spacing60 />
                  {!!share.label && <ShareCta>{share.label}</ShareCta>}
                  <ShareBtn
                    onClick={shareScroll}
                    id={useFieldId(elementId, `share-cta`)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 17 22"
                      width="17"
                      height="22">
                      <path d="m14 3-11 8 11 8" />
                      <circle cx="3" cy="11" r="3" />
                      <circle cx="14" cy="19" r="3" />
                      <circle cx="14" cy="3" r="3" />
                    </svg>
                  </ShareBtn>
                </>
              )}
            </HeroCopy>
          </>
        )}
        {openHoursLabel && (
          <FadeInWrapper delay={0.75}>
            <Spacing60 />
            <HeroCopy delay={openHoursLabel ? delayObj.content : 0}>
              <HeroOpenHours
                className="HeroOpenHours"
                id={useFieldId(elementId, `open-hours-label`)}>
                {openHoursLabel}
              </HeroOpenHours>
            </HeroCopy>
          </FadeInWrapper>
        )}
        {ctaLabel && ctaUrl && (
          <>
            <Spacing60 />
            <HeroCta isLink delay={delayObj.cta}>
              <Link
                id={useFieldId(elementId, `cta`)}
                to={ctaUrl}
                onClick={() => {
                  trackGTM('CTA', 'cta interaction', 'hero cta', ctaLabel)
                }}>
                <CtaLabel>{ctaLabel}</CtaLabel>
              </Link>
            </HeroCta>
          </>
        )}
        {ctaLabel && isBrandDirectory && toggleBrandSearch && (
          <>
            <Spacing60 />
            <HeroCta delay={delayObj.cta}>
              <Button
                id={useFieldId(elementId, `search-cta`)}
                onClick={onSearchHeroClick}
                level="primary"
                className="HeroCta"
                fixedSize>
                {ctaLabel}
                <SearchButtonIcon width={[10]} height={[10]} />
              </Button>
            </HeroCta>
          </>
        )}
        {ctaLabel && variant === 'memDays' && (
          <ExploreCue>
            <Eyebrow>{ctaLabel}</Eyebrow>
          </ExploreCue>
        )}
        {isCondensed && !openHoursLabel && (
          <FadeInWrapper delay={0.75}>
            <CenteredContainer>
              <Arrow
                {...arrowProps}
                icon={<ArrowSettingsIcon transform="rotate(270deg)" />}
                onClick={() => redirectToOffers()}
              />
            </CenteredContainer>
          </FadeInWrapper>
        )}
      </HeroBody>
      <BottomWhiteCurvedEdge />
    </HeroWrapper>
  )
}

Hero.propTypes = {
  audiences: PropTypes.string,
  isCondensed: PropTypes.bool,
  opacity: PropTypes.string,
  content: PropTypes.string,
  image: PropTypes.shape({
    altText: PropTypes.string,
    landscape: PropTypes.shape({
      gatsbyImageData: PropTypes.shape({
        aspectRatio: PropTypes.number.isRequired,
        sizes: PropTypes.string.isRequired,
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string.isRequired,
        srcSetWebp: PropTypes.string.isRequired,
      }).isRequired,
    }),
    portrait: PropTypes.shape({
      gatsbyImageData: PropTypes.shape({
        aspectRatio: PropTypes.number.isRequired,
        sizes: PropTypes.string.isRequired,
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string.isRequired,
        srcSetWebp: PropTypes.string.isRequired,
      }).isRequired,
    }),
  }),
  ctaLabel: PropTypes.string,
  ctaUrl: PropTypes.string,
  eyebrow: PropTypes.string,
  fullScreen: PropTypes.bool,
  isBrandDirectory: PropTypes.bool,
  logo: PropTypes.shape({ code: PropTypes.shape({ code: PropTypes.string }) }),
  pageTemplate: PropTypes.string,
  share: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
  }),
  toggleBrandSearch: PropTypes.func,
  video: PropTypes.shape({
    portrait: PropTypes.string,
    landscape: PropTypes.string,
    opacity: PropTypes.number,
    name: PropTypes.string,
  }),
  villageSlug: PropTypes.string,
  openHoursLabel: PropTypes.string,
}

Hero.defaultProps = {
  audiences: '',
  isCondensed: false,
  opacity: '',
  content: '',
  ctaLabel: '',
  ctaUrl: '',
  eyebrow: '',
  fullScreen: false,
  image: { portrait: {}, landscape: {}, altText: '' },
  isBrandDirectory: false,
  logo: { code: { code: '' } },
  pageTemplate: '',
  share: null,
  toggleBrandSearch: null,
  video: { portrait: '', landscape: '', opacity: null },
  villageSlug: '',
  openHoursLabel: '',
}

export default Hero
